import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ShieldCheck,
  Clock,
  BadgeDollarSign,
  Wrench,
  HeartHandshake,
  MapPin,
  CalendarCheck,
  Phone,
  CheckCircle2,
} from "lucide-react";
import { BUSINESS } from "@/lib/contact";
import heroImg from "@/assets/hero-technician.webp";

export const Route = createFileRoute("/about")({
  component: AboutPage,
  head: () => ({
    meta: [
      { title: "About StillFusion | Sacramento Appliance Repair" },
      {
        name: "description",
        content:
          "Locally owned appliance repair business serving Sacramento and surrounding cities. Honest pricing, quality parts, and friendly technicians.",
      },
      { property: "og:title", content: "About StillFusion Appliance Repair" },
      { property: "og:description", content: "Local technicians you can trust." },
      { property: "og:url", content: "https://stillfusionappliance.com/about" },
    ],
    links: [
      { rel: "canonical", href: "https://stillfusionappliance.com/about" },
    ],
  }),
});

const values = [
  { icon: ShieldCheck, title: "Honesty first", text: "Upfront pricing, no surprise fees, and no recommended repairs you don't actually need." },
  { icon: Clock, title: "Respect your time", text: "Tight arrival windows, real updates, and most repairs finished in a single visit." },
  { icon: BadgeDollarSign, title: "Fair pricing", text: "Competitive rates with quality parts — and a clear estimate before any work begins." },
  { icon: Wrench, title: "Quality work", text: "Years of hands-on experience across all major brands. We fix it right the first time." },
  { icon: HeartHandshake, title: "Friendly service", text: "We treat your home like our own — clean shoes, clean workspace, and clear communication." },
  { icon: MapPin, title: "Local & responsive", text: "Based in Sacramento, serving the entire metro and surrounding cities up to 75 miles." },
];

const stats = [
  { value: "1000+", label: "Repairs completed" },
  { value: "5.0★", label: "Average rating" },
  { value: "Same-day", label: "Most appointments" },
  { value: "All major", label: "Brands serviced" },
];

function AboutPage() {
  return (
    <>
      {/* Hero */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 sm:py-20 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              About Us
            </span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
              A local team that actually picks up the phone
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              {BUSINESS.name} is a {BUSINESS.city.split(",")[0]}-based appliance
              repair company founded by {BUSINESS.owner}. We started with a
              simple idea: appliance repair should be fast, honest, and easy —
              the way you'd want a friend to handle it.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" className="btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
                <CalendarCheck className="h-4 w-4" /> Book a Visit
              </Link>
              <a href={`tel:${BUSINESS.phone}`} className="btn-outline inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </div>
          </div>
          <div className="surface-elevated overflow-hidden">
            <img src={heroImg} alt="StillFusion appliance repair technician" className="aspect-[5/4] w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="surface-card p-6 text-center">
              <div className="text-3xl font-bold text-primary">{s.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              What we stand for
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Built on trust, finished with care
            </h2>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {values.map(({ icon: Icon, title, text }) => (
              <div key={title} className="surface-card p-6">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent/15 text-accent">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-semibold">{title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service area */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="space-y-10">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
              Service area
            </span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              Where we service
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
              We cover the greater {BUSINESS.city.split(",")[0]} area and
              surrounding cities — typically within 75 miles. If you're outside
              the area, give us a call anyway and we'll do our best.
            </p>
            <ul className="mx-auto mt-8 grid max-w-3xl grid-cols-2 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-4">
              {["Sacramento", "Roseville", "Rocklin", "Folsom", "Elk Grove", "Antelope", "Citrus Heights", "Carmichael"].map((c) => (
                <li key={c} className="flex items-center justify-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-accent" /> {c}
                </li>
              ))}
            </ul>
          </div>
          <div className="surface-elevated bg-hero-gradient p-10 text-center text-white sm:p-14">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Need a repair today?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-white/80">
              Most appointments are booked the same or next day. Reach out —
              we'll find a window that works.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-accent inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
                <CalendarCheck className="h-4 w-4" /> Book Now
              </Link>
              <a href={`tel:${BUSINESS.phone}`} className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold backdrop-blur transition hover:bg-white/15">
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
